import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSettings, updateServicesOrder } from '../../../api/settings';
import { resolveIcon } from '../../../components/tiles/IconPicker';
// Tuiles de module de l'onglet Services, dans l'ordre par défaut de l'app
// (ALL_MODULES de mobile/src/screens/modules/ModulesScreen.tsx).
const MODULE_TILES = [
    { key: 'actualites', label: 'Actualités' },
    { key: 'agenda', label: 'Agenda' },
    { key: 'annuaire', label: 'Annuaire' },
    { key: 'signalement', label: 'Signalements' },
    { key: 'demarches', label: 'Démarches' },
    { key: 'vie_scolaire', label: 'Vie scolaire' },
    { key: 'alertes', label: 'Alertes' },
    { key: 'messagerie_tickets', label: 'Contacter la mairie' },
    { key: 'messagerie_groups', label: 'Discussions' },
    { key: 'sondages', label: 'Sondages' },
    { key: 'projets_citoyens', label: 'Projets' },
    { key: 'urgences_sante', label: 'Urgences & Santé' },
    { key: 'meteo', label: 'Météo' },
    { key: 'travaux', label: 'Travaux' },
    { key: 'affichage_legal_acts', label: 'Affichage légal' },
    { key: 'affichage_legal_council', label: 'Conseil municipal' },
];
// Même règle de visibilité que l'app.
function isModuleVisible(key, enabled, messaging) {
    if (key === 'messagerie_tickets')
        return enabled.includes('messagerie') && messaging.tickets_enabled !== false;
    if (key === 'messagerie_groups')
        return enabled.includes('messagerie') && messaging.groups_enabled === true;
    if (key === 'affichage_legal_acts' || key === 'affichage_legal_council')
        return enabled.includes('affichage_legal');
    return enabled.includes(key);
}
export function ServicesOrder({ tiles }) {
    const queryClient = useQueryClient();
    const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: getSettings });
    const [items, setItems] = useState([]);
    const [saved, setSaved] = useState(false);
    // Ordre enregistré, puis les tuiles absentes de la liste dans l'ordre par défaut.
    const initial = useMemo(() => {
        if (!settings)
            return [];
        const s = settings.data;
        const enabled = s.enabled_modules ?? [];
        const messaging = s.messaging_config ?? {};
        const order = s.services_order ?? [];
        const visible = [
            ...MODULE_TILES.filter((m) => isModuleVisible(m.key, enabled, messaging)),
            ...tiles.filter((t) => t.published).map((t) => ({ key: `custom-${t.id}`, label: t.title, icon: t.icon, color: t.color })),
        ];
        const rank = (k) => { const i = order.indexOf(k); return i === -1 ? Number.MAX_SAFE_INTEGER : i; };
        return visible
            .map((item, i) => ({ item, i }))
            .sort((a, b) => rank(a.item.key) - rank(b.item.key) || a.i - b.i)
            .map(({ item }) => item);
    }, [settings, tiles]);
    useEffect(() => { setItems(initial); }, [initial]);
    const mut = useMutation({
        mutationFn: () => updateServicesOrder(items.map((i) => i.key)),
        onMutate: () => setSaved(false),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['settings'] });
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        },
    });
    const move = (idx, dir) => {
        setSaved(false);
        setItems((prev) => {
            const next = [...prev];
            [next[idx], next[idx + dir]] = [next[idx + dir], next[idx]];
            return next;
        });
    };
    const dirty = items.map((i) => i.key).join() !== initial.map((i) => i.key).join();
    if (!settings || items.length === 0)
        return null;
    return (_jsxs("div", { className: "mt-6 bg-white rounded-xl border border-gray-100 shadow-sm p-6", children: [_jsx("h2", { className: "text-[15px] font-semibold text-gray-700 mb-1", children: "Ordre des services" }), _jsx("p", { className: "text-xs text-gray-400 mb-4", children: "Ordre des tuiles dans l'onglet Services de l'app : modules activ\u00E9s et tuiles personnalis\u00E9es publi\u00E9es. Une tuile ajout\u00E9e plus tard se place \u00E0 la fin." }), _jsx("ol", { className: "space-y-1.5 mb-4", children: items.map((item, idx) => {
                    const Icon = item.icon ? resolveIcon(item.icon) : null;
                    return (_jsxs("li", { className: "flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg", children: [_jsx("span", { className: "w-5 text-xs text-gray-400 text-right", children: idx + 1 }), Icon && _jsx(Icon, { size: 16, color: item.color }), _jsxs("span", { className: "flex-1 text-sm text-gray-700", children: [item.label, item.key.startsWith('custom-') && _jsx("span", { className: "ml-2 text-xs text-gray-400", children: "tuile personnalis\u00E9e" })] }), _jsx("button", { type: "button", onClick: () => move(idx, -1), disabled: idx === 0, "aria-label": `Monter ${item.label}`, className: "w-7 h-7 flex items-center justify-center border rounded text-gray-400 disabled:opacity-30 hover:bg-white text-xs", children: "\u2191" }), _jsx("button", { type: "button", onClick: () => move(idx, 1), disabled: idx === items.length - 1, "aria-label": `Descendre ${item.label}`, className: "w-7 h-7 flex items-center justify-center border rounded text-gray-400 disabled:opacity-30 hover:bg-white text-xs", children: "\u2193" })] }, item.key));
                }) }), saved && _jsx("p", { className: "text-sm text-green-600 mb-2", children: "Ordre des services enregistr\u00E9." }), _jsx("button", { onClick: () => mut.mutate(), disabled: !dirty || mut.isPending, className: "px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50", children: mut.isPending ? 'Enregistrement...' : "Enregistrer l'ordre" })] }));
}
